import React, { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react'

interface InteractionContextValue {
  /**
   * Whether the input field is currently focused
   */
  isInputFocused: boolean
  
  /**
   * Whether suggestions are currently visible/expanded
   */
  areSuggestionsVisible: boolean
  
  /**
   * Whether the chat has started (first message sent)
   */
  hasStartedChat: boolean
  
  /**
   * Whether the chat is collapsed (mobile floating mode)
   */
  isChatCollapsed: boolean
  
  /**
   * Update input focus state
   */
  setInputFocused: (focused: boolean) => void
  
  /**
   * Update suggestions visibility
   */
  setSuggestionsVisible: (visible: boolean) => void
  
  /**
   * Mark chat as started
   */
  setChatStarted: (started: boolean) => void
  
  /**
   * Toggle chat collapsed state
   */
  setChatCollapsed: (collapsed: boolean) => void
  
  /**
   * Register the widget container element for click-outside detection
   */
  setWidgetContainer: (element: HTMLElement | null) => void
  
  /**
   * Combined state: whether we're in an "expanded" state
   * (input focused or suggestions visible)
   */
  isExpanded: boolean
}

const InteractionContext = createContext<InteractionContextValue | undefined>(undefined)

interface InteractionProviderProps {
  children: React.ReactNode
}

/**
 * Provider for tracking user interaction states across the chat widget.
 * This helps components coordinate their appearance based on user activity.
 */
export function InteractionProvider({ children }: InteractionProviderProps) {
  const [isInputFocusedState, setIsInputFocusedState] = useState(false)
  const [areSuggestionsVisible, setAreSuggestionsVisible] = useState(false)
  const [hasStartedChat, setHasStartedChat] = useState(false)
  const [isChatCollapsed, setIsChatCollapsed] = useState(false)
  const widgetContainerRef = useRef<HTMLElement | null>(null)
  
  const setInputFocused = useCallback((focused: boolean) => {
    setIsInputFocusedState(focused)
    // When input is focused, expand the chat if it was collapsed
    if (focused && isChatCollapsed) {
      setIsChatCollapsed(false)
    }
  }, [isChatCollapsed])
  
  const setSuggestionsVisible = useCallback((visible: boolean) => {
    setAreSuggestionsVisible(visible)
  }, [])
  
  const setChatStarted = useCallback((started: boolean) => {
    setHasStartedChat(started)
  }, [])
  
  const setChatCollapsed = useCallback((collapsed: boolean) => {
    setIsChatCollapsed(collapsed)
    // When collapsing, reset interaction states
    if (collapsed) {
      setIsInputFocusedState(false)
      setAreSuggestionsVisible(false)
    }
  }, [])
  
  const setWidgetContainer = useCallback((element: HTMLElement | null) => {
    widgetContainerRef.current = element
  }, [])
  
  const value = useMemo<InteractionContextValue>(() => ({
    isInputFocused: isInputFocusedState,
    areSuggestionsVisible,
    hasStartedChat,
    isChatCollapsed,
    setInputFocused,
    setSuggestionsVisible,
    setChatStarted,
    setChatCollapsed,
    setWidgetContainer,
    // Expanded state: when user is actively interacting
    isExpanded: isInputFocusedState || areSuggestionsVisible
  }), [
    isInputFocusedState, 
    areSuggestionsVisible, 
    hasStartedChat,
    isChatCollapsed,
    setInputFocused,
    setSuggestionsVisible,
    setChatStarted,
    setChatCollapsed,
    setWidgetContainer
  ])
  
  return (
    <InteractionContext.Provider value={value}>
      {children}
    </InteractionContext.Provider>
  )
}

/**
 * Hook to access interaction state throughout the component tree
 */
export function useInteraction() {
  const context = useContext(InteractionContext)
  
  if (!context) {
    throw new Error('useInteraction must be used within InteractionProvider')
  }
  
  return context
}
